import { api } from '../../../api/api';

const TRADE_TAG = 'Trade';
const PAYOUT_TAG = 'TradePayout';

export type TradeProfile = {
  success: boolean;
  business_name: string;
  contact_name: string | null;
  email: string;
  referral_code: string;
  referral_link: string;
  commission_rate: number;
};

export type TradeCustomer = {
  email: string;
  status: 'activated' | 'pending';
  sims: string[];
  plans: string[];
  stripe_customer_ids: string[];
  referred_at: string | null;
  total_paid: number; // pence
  invoice_count: number;
  commission: number; // pence
  currency: string;
  revenue_available: boolean;
};

export type TradeCustomersResponse = {
  success: boolean;
  commission_rate: number;
  currency: string;
  summary: {
    total_customers: number;
    activated_customers: number;
    pending_customers: number;
    total_revenue: number; // pence
    total_commission: number; // pence
  };
  monthly_revenue: Array<{
    month: string; // 'YYYY-MM'
    revenue: number; // pence
    commission: number; // pence
  }>;
  customers: TradeCustomer[];
};

export type TradePayout = {
  success: boolean;
  has_details: boolean;
  account_name?: string;
  account_number_masked?: string;
  sort_code?: string;
  updated_at?: string | null;
};

type LoginResponse = { success: boolean; message: string; token: string };
type RequestLinkResponse = { success: boolean; message: string; magicLink?: string };
type MessageResponse = { success: boolean; message: string };
type RegisterResponse = {
  success: boolean;
  message: string;
  referral_code: string;
  referral_link: string;
  magicLink?: string;
};

export const tradeApi = api.enhanceEndpoints({ addTagTypes: [TRADE_TAG, PAYOUT_TAG] }).injectEndpoints({
  endpoints: (build) => ({
    tradeRegister: build.mutation<
      RegisterResponse,
      { business_name: string; email: string; contact_name?: string }
    >({
      query: (body) => ({
        method: 'POST',
        url: 'trade/register',
        body
      })
    }),
    tradeRequestLogin: build.mutation<RequestLinkResponse, { email: string }>({
      query: (body) => ({
        method: 'POST',
        url: 'trade/request-login',
        body
      })
    }),
    tradeLogin: build.query<LoginResponse, { token: string }>({
      query: ({ token }) => ({
        method: 'GET',
        url: 'trade/login',
        params: { token }
      })
    }),
    tradeMe: build.query<TradeProfile, void>({
      query: () => ({
        method: 'GET',
        url: 'trade/me'
      }),
      providesTags: [TRADE_TAG]
    }),
    tradeCustomers: build.query<TradeCustomersResponse, void>({
      query: () => ({
        method: 'GET',
        url: 'trade/customers'
      }),
      providesTags: [TRADE_TAG]
    }),
    tradeAddCustomer: build.mutation<MessageResponse, { email: string }>({
      query: (body) => ({
        method: 'POST',
        url: 'trade/add-customer',
        body
      }),
      invalidatesTags: [TRADE_TAG]
    }),
    tradePayout: build.query<TradePayout, void>({
      query: () => ({
        method: 'GET',
        url: 'trade/payout'
      }),
      providesTags: [PAYOUT_TAG]
    }),
    tradeSavePayout: build.mutation<
      MessageResponse,
      { account_name: string; account_number: string; sort_code: string }
    >({
      query: (body) => ({
        method: 'POST',
        url: 'trade/payout',
        body
      }),
      invalidatesTags: [PAYOUT_TAG]
    })
  })
});

export const {
  useTradeRegisterMutation,
  useTradeRequestLoginMutation,
  useLazyTradeLoginQuery,
  useTradeMeQuery,
  useTradeCustomersQuery,
  useTradeAddCustomerMutation,
  useTradePayoutQuery,
  useTradeSavePayoutMutation
} = tradeApi;
